import React, { useEffect, useMemo, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Search, RotateCcw, Filter, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Barre de recherche rapide réutilisable (client-side).
 *
 * Props:
 *   value:    objet { q: string, ...extras } — l'état courant des filtres (contrôlé par le parent)
 *   onChange: (newValue) => void
 *   filters:  Array<{
 *               key: string,
 *               label: string,
 *               type: "select" | "date",
 *               options?: Array<{ value: string, label: string }>,
 *               testid?: string,
 *               placeholder?: string,
 *             }>
 *   placeholder: string — placeholder de la zone de recherche libre
 *   testid:      string — prefix pour les data-testid
 *   collapsibleFilters: bool (défaut true) — repli les filtres pour économiser l'espace sur mobile
 *   showReset: bool (défaut true)
 */
export default function QuickSearchBar({
  value,
  onChange,
  filters = [],
  placeholder = "Rechercher…",
  testid = "quick-search",
  collapsibleFilters = true,
  showReset = true,
  renderExtraFilters = null,
}) {
  const [expanded, setExpanded] = useState(false);

  const activeCount = useMemo(() => {
    let n = 0;
    for (const f of filters) {
      if (value?.[f.key]) n += 1;
    }
    return n;
  }, [value, filters]);

  // Déplie automatiquement si l'utilisateur applique un filtre depuis l'URL ou via reset
  useEffect(() => {
    if (activeCount > 0) setExpanded(true);
  }, [activeCount]);

  const set = (key, v) => onChange({ ...(value || {}), [key]: v });
  const reset = () => {
    const cleared = { q: "" };
    for (const f of filters) cleared[f.key] = "";
    onChange(cleared);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-3 sm:p-4" data-testid={`${testid}-bar`}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            type="text"
            value={value?.q || ""}
            onChange={(e) => set("q", e.target.value)}
            placeholder={placeholder}
            className="pl-8"
            data-testid={`${testid}-input`}
          />
        </div>
        {filters.length > 0 && collapsibleFilters && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setExpanded((x) => !x)}
            className="flex-shrink-0"
            data-testid={`${testid}-toggle`}
          >
            <Filter size={13} className="mr-1.5" />
            Filtres
            {activeCount > 0 && (
              <span className="ml-1.5 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-800 border border-blue-200">
                {activeCount}
              </span>
            )}
            {expanded ? <ChevronUp size={12} className="ml-1" /> : <ChevronDown size={12} className="ml-1" />}
          </Button>
        )}
        {showReset && (value?.q || activeCount > 0) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={reset}
            className="text-zinc-600 hover:text-zinc-900 flex-shrink-0"
            data-testid={`${testid}-reset`}
            title="Réinitialiser"
          >
            <RotateCcw size={13} />
          </Button>
        )}
      </div>

      {(filters.length > 0 || renderExtraFilters) && (!collapsibleFilters || expanded) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3 pt-3 border-t border-zinc-100">
          {filters.map((f) => (
            <div key={f.key}>
              <Label className="label-uppercase mb-1.5 block">{f.label}</Label>
              {f.type === "date" ? (
                <Input
                  type="date"
                  value={value?.[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  data-testid={f.testid || `${testid}-${f.key}`}
                />
              ) : (
                <Select
                  value={value?.[f.key] || "_all"}
                  onValueChange={(v) => set(f.key, v === "_all" ? "" : v)}
                >
                  <SelectTrigger data-testid={f.testid || `${testid}-${f.key}`}>
                    <SelectValue placeholder={f.placeholder || "Tous"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">{f.placeholder || "Tous"}</SelectItem>
                    {(f.options || []).map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}
          {typeof renderExtraFilters === "function" && renderExtraFilters()}
        </div>
      )}
    </div>
  );
}

/**
 * Hook utilitaire : filtre une liste en fonction de l'état `filters`.
 *
 *   const filtered = useQuickFilter(items, filters, {
 *     text: (item) => `${item.name} ${item.description}`,    // accesseur pour la recherche libre
 *     predicates: {                                          // un prédicat par filtre clé
 *       brand_id: (item, value) => item.brand_id === value,
 *       date_from: (item, value) => item.date >= value,
 *     },
 *   });
 */
export function useQuickFilter(items, filters, { text, predicates = {} } = {}) {
  return useMemo(() => {
    if (!Array.isArray(items)) return [];
    const q = (filters?.q || "").trim().toLowerCase();
    return items.filter((item) => {
      if (q && text) {
        const haystack = String(text(item) || "").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      for (const [key, value] of Object.entries(filters || {})) {
        if (key === "q" || !value) continue;
        const pred = predicates[key];
        if (pred && !pred(item, value)) return false;
      }
      return true;
    });
  }, [items, filters, text, predicates]);
}

export const EMPTY_FILTERS = (extra = {}) => ({ q: "", ...extra });
